import { formatDate } from "common/service/CommonService";
import CommonStatusBadge from "Components/Common/CommonStatusBadge";
import { formatDateTimeLocal } from "helpers/constant";
import ip from "modules/Pharmacy/IPinventory/css/ipInventory.module.scss";

export const reprintColumn = ( 
  selectedRows: string[],
  toggleRowSelect: (printid: string) => void,
  handleReprintClick: (row: any) => void,
  canprint?: boolean,
  activeTab?: string
) => {
  return [
    ...(canprint
      ? [
          {
            key: "select",
            label: "REPRINT" as any,
            render: (_: any, row?: any) => {
              if (!row) return null;

              return (
                <input
                  type="checkbox"
                  checked={selectedRows.includes(row.uniqueKey || row.printid)}
                  onChange={() => toggleRowSelect(row.uniqueKey || row.printid)}
                  style={{ cursor: "pointer" }}
                />
              );
            },
          },
        ]
      : []),
    {
      key: "printid",
      label: "PRINT ID",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <span style={{ fontWeight: 600, color: "#333", fontSize: 13 }}>
            {row.printid}
          </span>
        );
      },
    },
    {
      key: "printby",
      label: "PRINTED BY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <div style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>
            <div
              style={{
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <i
                className="ri-user-line"
                style={{ fontSize: 12, color: "#008080" }}
              />
              {row.printby || "—"}
            </div>
          </div>
        );
      },
    },
    {
      key: "timestamp",
      label: "PRINTED ON DATE & TIME",
      render: (_: any, row?: any) => {
        if (!row) return null;

        const rawVal = row.printdate || "";
        if (!rawVal) {
          return (
            <div style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>
              <div style={{ fontWeight: 600 }}>-</div>
            </div>
          );
        }

        // Construct UTC ISO string for UTC parsing (same as formatDateTimeLocal)
        let utcStr = String(rawVal).trim();
        if (!utcStr.includes("T")) {
          const timeVal = row.printtime ? String(row.printtime).trim().split(".")[0] : "00:00:00";
          utcStr = `${utcStr}T${timeVal}Z`;
        } else if (!utcStr.endsWith("Z") && !utcStr.includes("+")) {
          utcStr = utcStr + "Z";
        }

        const d = new Date(utcStr);

        if (isNaN(d.getTime())) {
          return (
            <div style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>
              <div style={{ fontWeight: 600 }}>{formatDate(row.printdate)}</div>
            </div>
          );
        }

        const dateStr = formatDate(d);
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        const ss = String(d.getSeconds()).padStart(2, "0");
        const timeStr = `${hh}:${mm}:${ss}`;

        return (
          <div style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>
            <div style={{ fontWeight: 600 }}>{dateStr}</div>
            <div className="text-muted" style={{ fontSize: 12, fontWeight: 500, marginTop: 1 }}>
              {timeStr}
            </div>
          </div>
        );
      },
    },
    {
      key: "subjectid",
      label: "SUBJECT / STUDY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        const subjNo =
          Number(row.subjectno) >= 1 && Number(row.subjectno) <= 9
            ? String(row.subjectno).padStart(2, "0")
            : row.subjectno || "—";

        const pNo = row.period_number ? `P:${String(row.period_number).padStart(2, "0")}` : null;
        const tpNo = row.timepoint_number ? `T:${row.timepoint_number}` : null;
        const aliNo = row.aliquot_number || null;

        const extraInfo = [pNo, tpNo, aliNo].filter(Boolean).join(" | ");

        return (
          <>
            <p className={ip.cellPrimary} style={{ fontWeight: 700, marginBottom: 2 }}>
              {subjNo}
            </p>
            <p className={ip.cellSecondary}>
              {row.studycode}
              {extraInfo ? ` (${extraInfo})` : ""}
            </p>
          </>
        );
      },
    },
    {
      key: "barcode",
      label: "BARCODE",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <span style={{ fontWeight: 600, color: "#333", fontSize: 13 }}>
            {row.barcode || "—"}
          </span>
        );
      },
    },
    {
      key: "qty",
      label: "QTY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <span
            style={{
              display: "inline-block",
              fontWeight: 700,
              color: "#111827",
              fontSize: 13,
              background: "#eff6ff",
              padding: "2px 10px",
              borderRadius: 20,
            }}
          >
            {row.printcount || 1}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "STATUS",
      render: (_: any, row?: any) => {
        if (!row) return null;
        const isPrinted = (row.printstatus || "").toLowerCase() === "printed";
        return (
          <CommonStatusBadge
            isActive={isPrinted}
            activeText="Printed"
            inactiveText="Pending"
            activeColor="success"
            inactiveColor="warning"
          />
        );
      },
    },
    ...(canprint
      ? [
          {
            key: "action",
            label: "ACTION",
            render: (_: any, row?: any) => {
              if (!row) return null;

              return (
                <button
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{
                    fontSize: 12,
                    borderRadius: 6,
                    border: "1px solid #1e6b91",
                    color: "#1e6b91",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                      "#1e6b91";
                    (e.currentTarget as HTMLButtonElement).style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                      "transparent";
                    (e.currentTarget as HTMLButtonElement).style.color = "#1e6b91";
                  }}
                  onClick={() => handleReprintClick(row)}
                >
                  <i className="ri-printer-line fs-13" />
                  Reprint
                </button>
              );
            },
          },
        ]
      : []),
  ];
};
 